/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { Fragment } from 'react';

import { styled } from '@compiled/react';
import { FeatureGateLogo } from '@jira/navigation-apps__atlassian-navigation-ui/src/ui/plan-feature-gate/index.tsx';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import CustomThemeButton, {
	type CustomThemeButtonProps,
} from '@atlaskit/button/custom-theme-button';
import Heading from '@atlaskit/heading/heading';
import { FlyoutBody, FlyoutHeader } from '@atlaskit/side-nav-items/flyout-menu-item';
import PremiumIcon from '@atlaskit/icon/core/premium';
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { PremiumUpgradeModalEntryPointButton } from '@jira/growth__premium-upgrade-modal/src/ui/premium-upgrade-modal-entry-point-button/index.tsx';
import {
	fireUIAnalytics,
	FireScreenAnalytics,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';

const plansHeading = defineMessage({
	id: 'atlassian-navigation.plans.menu.premium-feature-gate.plans-heading',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Plans',
	description:
		'Section heading displayed at the top of the Plans dropdown menu in the top navigation bar',
});

const title = defineMessage({
	id: 'atlassian-navigation.plans.menu.premium-feature-gate.title',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Unlock advanced planning tools',
	description:
		'Heading of the content of the "Plans" menu for Standard or Free edition, inviting users to upgrade to Premium.',
});

const imageAlt = defineMessage({
	id: 'atlassian-navigation.plans.menu.premium-feature-gate.image-alt',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Upgrade to Jira Premium',
	description: 'Alt text for upgrade to premium plans menu.',
});

const description = defineMessage({
	id: 'atlassian-navigation.plans.menu.premium-feature-gate.description',
	defaultMessage:
		// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
		"Boost agility with capacity planning, dependency mapping, and more. Try Jira Premium's high-level cross-team planning tools.",
	description: 'Description of the Jira Software Premium instance advantages, with focus on plans.',
});

const learnMoreLink = defineMessage({
	id: 'atlassian-navigation.plans.menu.premium-feature-gate.learn-more-link',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Read the guide',
	description: 'Link to learn more about Jira Software Premium.',
});

const startTrialButton = defineMessage({
	id: 'atlassian-navigation.plans.menu.premium-feature-gate.start-trial-button',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Try it free',
	description: 'Button to try Jira Software Premium.',
});

const closeButton = defineMessage({
	id: 'atlassian-navigation.plans.menu.premium-feature-gate.close-button',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Close plans menu',
	description: 'Message for the close button in the plans menu.',
});

const LEARN_MORE_LINK =
	'https://www.atlassian.com/software/jira/guides/advanced-roadmaps/overview#what-are-advanced-roadmaps';

const PremiumFeatureGate = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { colorMode } = useThemeObserver();

	const onStartTrialClick = (_: unknown, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'plansNavigationMenuItem', {
			level: 2,
			componentType: 'fixedItemAction',
			parentItemId: 'plans',
			itemId: 'tryItFree',
		});
	};

	const onLearnMoreClick = (_: unknown, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'plansNavigationMenuItem', {
			level: 2,
			componentType: 'fixedItemAction',
			parentItemId: 'plans',
			itemId: 'readTheGuide',
		});
	};

	const FlyoutBodyBehindFG = fg('platform_dst_nav4_flyout_menu_slots_close_button')
		? FlyoutBody
		: Fragment;

	return (
		<ContextualAnalyticsData sourceName="plansPremiumFeatureGate" sourceType="Modal">
			{fg('platform_dst_nav4_flyout_menu_slots_close_button') && (
				<FlyoutHeader
					title={formatMessage(plansHeading)}
					closeButtonLabel={formatMessage(closeButton)}
				/>
			)}
			<FlyoutBodyBehindFG>
				<MenuContainer>
					<MenuHeaderContainer>
						<FeatureGateLogo alt="" colorMode={colorMode} />
					</MenuHeaderContainer>
					<ContentContainer>
						<Heading size="small" as="h2">
							{formatMessage(title)}
						</Heading>
						<Box xcss={contentDescriptionStyles}>{formatMessage(description)}</Box>
					</ContentContainer>
					<FooterContainer>
						<LearnMoreButton
							appearance="link"
							href={LEARN_MORE_LINK}
							target="_blank"
							onClick={onLearnMoreClick}
						>
							{formatMessage(learnMoreLink)}
						</LearnMoreButton>
						<TryPremiumButton
							onClick={onStartTrialClick}
							iconBefore={
								<PremiumIcon label={formatMessage(imageAlt)} color={token('color.text.inverse')} />
							}
						>
							{formatMessage(startTrialButton)}
						</TryPremiumButton>
					</FooterContainer>
				</MenuContainer>
			</FlyoutBodyBehindFG>
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	);
};

export default PremiumFeatureGate;

const TryPremiumButton = (props: CustomThemeButtonProps) => (
	<PremiumUpgradeModalEntryPointButton
		{...props}
		theme={(current, themeProps) => ({
			buttonStyles: {
				...current(themeProps).buttonStyles,
				display: 'flex',
				alignItems: 'center',
				background: `linear-gradient(135deg, ${token('color.background.discovery.bold')}, ${token('color.background.brand.bold')})`,
				color: token('color.text.inverse'),
				outline: 'none',
				boxSizing: 'border-box',
				padding: `${token('space.050')} ${token('space.150')}`,
				borderRadius: token('radius.small'),
				height: token('space.400'),
				border: 'none',
				font: token('font.heading.xsmall'),
				gap: token('space.025'),
			},
			spinnerStyles: current(themeProps).spinnerStyles,
		})}
	/>
);

const LearnMoreButton = (props: CustomThemeButtonProps) => (
	<CustomThemeButton
		{...props}
		theme={(current, themeProps) => ({
			buttonStyles: {
				...current(themeProps).buttonStyles,
				maxWidth: '250px',
				marginRight: token('space.200'),
			},
			spinnerStyles: current(themeProps).spinnerStyles,
		})}
	/>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MenuContainer = styled.div({
	boxSizing: 'border-box',
	borderRadius: token('radius.small'),
	borderWidth: token('border.width'),
	borderStyle: 'solid',
	borderColor: 'transparent',
	background: `linear-gradient(135deg, ${token('color.background.accent.blue.subtle')}, ${token('color.background.accent.purple.subtle')})`,
	backgroundOrigin: 'border-box',
	backgroundClip: 'border-box',
	maxWidth: '397px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MenuHeaderContainer = styled.div({
	background: `linear-gradient(79deg,
        ${token('color.background.accent.blue.subtlest')},
        ${token('color.background.accent.purple.subtlest')})`,
	height: '90px',
	borderRadius: `${token('radius.small')} ${token('radius.small')} 0 0`,
	padding: 0,
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentContainer = styled.div({
	paddingTop: token('space.300'),
	paddingRight: token('space.300'),
	paddingBottom: token('space.300'),
	paddingLeft: token('space.300'),
	background: `${token('elevation.surface')}`,
});

const contentDescriptionStyles = xcss({
	marginTop: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterContainer = styled.div({
	paddingTop: 0,
	paddingRight: token('space.300'),
	paddingBottom: token('space.300'),
	paddingLeft: token('space.300'),
	background: `${token('elevation.surface')}`,
	borderRadius: `0 0 ${token('radius.small')} ${token('radius.small')}`,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
});
